import React, {useEffect, useState} from 'react';
import {GridSortModel} from '@mui/x-data-grid-pro';
import {useLocation, useSearchParams} from 'react-router-dom';
import StandardReports from '../../StandardReports';
import {t} from 'i18next';
import {
  mapSelectedUsers,
  mergeApiDataIntoConfig,
  getDocumentAuditData,
} from '../../common/ReportHelper';
import {ISelectItem} from '../../../../../shared/types';
import {
  useExportDocumentAuditReport,
  useGetDocumentAuditReportResultData,
  useSaveDocumentAuditReportData,
  useLeftPanelReport,
} from '../../../../../hooks/reporting/useReporting';
import {Reports, RightPanelConfig} from '../../config/reportTypes';
import {columns} from './column';
import dayjs from 'dayjs';
import {
  DocumentAuditReportRequestBody,
  DocumentAuditReportResponse,
  DocumentAuditSaveRequestPayload,
  ExportDocumentAuditReportRequestPayload,
} from '../../../../../api/types/reportingApi.type';
import {ToastType} from '../../../../../shared/constants';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import ArrWaitToCompleteModalWrapper from '../../../standard-reports/component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import './DocumentAuditContainer.scss';

interface ReportConfig {
  infoContent: string | string[] | undefined;
  title: string;
  infoModalTitle: string;
  showInfoIcon?: boolean;
  config: Reports;
}

const DEFAULT_OPTION: ISelectItem = {
  value: 'currentLoggedIn',
  label: 'Currently Logged-In Users',
};

export interface ISavePayload {
  reportTypeCode: string;
  selectedReviewers: number[];
  fromDateTime: string | null;
  toDateTime: string | null;
  showCurrentlyLoggedInUsers: number;
  selectedCustomColumns: number[];
  selectedSource: string;
  pageNumber: number;
  pageSize: number;
  sortingColumn: string;
  isAscending: string;
  reportID: number;
}

const REPORT_KEY = 'DocumentAudit';
const REPORT_TYPE_CODE = 'DAR004';

const ToastMessages = {
  docIdOrSearchesRequired: t(
    'docExplorer.actionMenu.bulkTaggingWarning.docAuditWarnings'
  ),
  docIdOrSearchesNotBoth: t(
    'docExplorer.actionMenu.bulkTaggingWarning.docAuditWarning'
  ),
  exportHasStarted: t('reports.common.reportExportSuccess'),
  exportHasFailed: t('reports.common.reportExportError'),
};

interface PageInfo {
  totalRecords: number;
  pageSize: number;
  pageNumber: number;
}

const DocumentAuditContainer: React.FC = () => {
  const [reportConfig, setReportConfig] = useState<ReportConfig | null>(null);
  const [dynamicData, setDynamicData] = useState<any>(null);
  const [saveReportPayload, setSaveReportPayload] = useState<ISavePayload>({
    reportTypeCode: '',
    selectedReviewers: [],
    fromDateTime: '',
    toDateTime: '',
    showCurrentlyLoggedInUsers: 0,
    selectedCustomColumns: [],
    selectedSource: '',
    pageNumber: 0,
    pageSize: 0,
    sortingColumn: '',
    isAscending: '',
    reportID: 0,
  });

  const [selectedOption, setSelectedOption] = useState<ISelectItem | null>(
    DEFAULT_OPTION
  );
  const [gridData, setGridData] = useState<DocumentAuditReportResponse | null>(
    null
  );
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [pageModel, setPageModel] = useState<PageInfo>({
    totalRecords: 0,
    pageSize: 10,
    pageNumber: 1,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const [notificationId, setNotificationId] = useState<number | null>(null);
  const [reportId, setReportId] = useState<number | null>(null);

  const {mutateAsync: saveDocumentAuditReportData} =
    useSaveDocumentAuditReportData();
  const {mutateAsync: getReportGridData} =
    useGetDocumentAuditReportResultData();
  const {mutateAsync: runDocumentAuditExport} = useExportDocumentAuditReport();

  const [isExporting, setIsExporting] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, any>>(
    {}
  );

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const {fromReportLanding = false} = location.state ?? {};
  const urlReportId = Number.parseInt(searchParams.get('ReportID') ?? '0');
  const urlReportName = searchParams.get('ReportLabel');
  const {data: leftPanelReport} = useLeftPanelReport(urlReportId);
  const [initialValues, setInitialValues] = useState<Record<string, any>>();
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  useEffect(() => {
    // 1. Load config
    loadReportConfig();
  }, []);

  useEffect(() => {
    // 2. Load config data
    if (reportConfig) {
      loadDynamicData();
    }
  }, [reportConfig]);

  useEffect(() => {
    // 3. Load saved cofig data
    if (dynamicData) {
      if (leftPanelReport) {
        // "actionTypes" are referenced by index, not name, so all indices must be filled.
        // If the name is present, set it to true. If not, set it to false.
        const formattedActionTypes = dynamicData.actionTypes.map(
          (data: any) => {
            const match = leftPanelReport.selected_ActionTypeID.find(
              (i) => i === data.actionTypeID
            );
            return match ? true : false;
          }
        );

        // "searchList" should be an array of strings but we get numbers
        const formattedSearchList = leftPanelReport.selected_SearchID.map(
          (i) => `${i}`
        );

        const values = {
          docID: leftPanelReport.docID,
          fromDate: leftPanelReport.fromDateTime,
          toDate: leftPanelReport.toDateTime,
          users: leftPanelReport.selected_UserID,
          searchList: formattedSearchList,
          actionTypes: formattedActionTypes,
        };
        setInitialValues(values);
        setSelectedFilters(values);

        if (!fromReportLanding) {
          loadReportResults(urlReportId);
        }
      }

      setIsInitialized(true);
    }
  }, [dynamicData, leftPanelReport, urlReportId]);

  const showSuccessMessage = (message: string) => {
    showToastMessage(message, ToastType.Success);
  };

  const showWarningMessage = (message: string) => {
    showToastMessage(message, ToastType.Warning);
  };

  const showErrorMessage = (message: string) => {
    showToastMessage(message, ToastType.Error);
  };

  const showToastMessage = (message: string, toastType: ToastType) => {
    setToastMessage(message);
    setToastStatus([toastType]);
    setShowToast(true);
  };

  const RIGHT_PANEL_HEADER_CONFIG: RightPanelConfig = {
    title: '',
    subTitle: `Data as of ${dayjs(new Date()).format('YYYY/MM/DD HH:mm:ss')} UTC`,
    showSaveIcon: true,
    showColumnsIcon: true,
    showActionButton: false,
    showUploadIcon: true,
  };

  const handleColumnToggleAction = async (columnId: string) => {
    setSaveReportPayload((prev) => {
      const targetCol = columns.find((col) => col.field === columnId);
      const targetIndex = targetCol?.index;
      if (typeof targetIndex !== 'number') {
        return prev;
      }
      let next = prev.selectedCustomColumns;
      if (next.includes(targetIndex)) {
        next = next.filter((id) => id !== targetIndex);
      } else {
        next.push(targetIndex);
      }
      next = Array.from(new Set(next));
      return {
        ...prev,
        selectedCustomColumns: next,
      };
    });
  };

  const handleAction = async (
    actionKey: string,
    values: Record<string, any>
  ) => {
    if (actionKey === 'applyChanges') {
      await handleApply(values);
    } else if (actionKey === 'reset') {
      handleReset();
    }
  };

  const loadReportConfig = async () => {
    try {
      const data = await import(`../../config/${REPORT_KEY}.json`);
      setReportConfig(data.report);
    } catch (error) {
      console.error('Error loading JSON:', error);
    }
  };

  const loadDynamicData = async () => {
    try {
      const data = await getDocumentAuditData();
      setDynamicData(data);
    } catch (error) {
      console.error('Error fetching API data:', error);
    }
  };

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let intervalId: number | undefined;
    let attached = false;

    const tryAttach = () => {
      const btn = document.querySelector('[aria-label="Export"]');
      if (!btn || !runDocumentAuditExport) {
        return false;
      }
      btn.addEventListener('click', handleExport);
      cleanup = () => btn.removeEventListener('click', handleExport);
      attached = true;
      return true;
    };

    // Attempt immediate attach, else retry briefly
    if (!tryAttach()) {
      let tries = 0;
      intervalId = window.setInterval(() => {
        tries += 1;
        if (tryAttach() || tries > 50) {
          if (intervalId) {
            window.clearInterval(intervalId);
          }
          intervalId = undefined;
        }
      }, 50);
    }

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
      if (cleanup && attached) {
        cleanup();
      }
    };
  }, [
    runDocumentAuditExport,
    saveReportPayload,
    gridData,
    isExporting,
    loading,
    reportConfig,
  ]);

  const handleApply = async (values: Record<string, any>) => {
    const hasDocId =
      values.docID !== undefined &&
      values.docID !== null &&
      values.docID !== '';

    const hasSearchIds =
      Array.isArray(values.searchList) && values.searchList.some(Boolean);

    const hasBothCriteria = hasDocId && hasSearchIds;
    const hasNeitherCriteria = !hasDocId && !hasSearchIds;

    if (hasBothCriteria || hasNeitherCriteria) {
      if (hasBothCriteria) {
        showWarningMessage(ToastMessages.docIdOrSearchesNotBoth);
      } else if (hasNeitherCriteria) {
        showWarningMessage(ToastMessages.docIdOrSearchesRequired);
      }

      setSelectedFilters({});
      setGridData(null);
      setSaveReportPayload({
        reportTypeCode: '',
        selectedReviewers: [],
        fromDateTime: '',
        toDateTime: '',
        showCurrentlyLoggedInUsers: 0,
        selectedCustomColumns: [],
        selectedSource: '',
        pageNumber: 0,
        pageSize: 0,
        sortingColumn: '',
        isAscending: '',
        reportID: 0,
      });

      return;
    }

    try {
      setGridData(null);
      setLoading(true);
      setSelectedFilters(values);

      // Resolve selected users
      const selectedUsers = mapSelectedUsers(
        values.users || [],
        dynamicData?.userDetails || []
      );

      const containerActionTypes = Array.isArray(dynamicData?.actionTypes)
        ? dynamicData.actionTypes.map(() => true)
        : [];

      const effectiveActionTypes = Array.isArray(values.actionTypes)
        ? values.actionTypes
        : containerActionTypes;

      const selectedActionTypes = effectiveActionTypes
        .map((checked: boolean, idx: number) =>
          checked ? dynamicData?.actionTypes?.[idx]?.actionTypeID : null
        )
        .filter((id: string | null) => id !== null);

      const selectedSearchIds = (values.searchList || [])
        .map((s: any, idx: number) => {
          if (typeof s === 'object' && s.id) {
            return Number(s.id);
          }
          if (typeof s === 'boolean' && s) {
            return Number(dynamicData?.searchList[idx]?.id);
          }
          if (typeof s === 'string' || typeof s === 'number') {
            return Number(s);
          }
          return null;
        })
        .filter((id: number | null) => id !== null && !isNaN(id));

      const savePayload: DocumentAuditSaveRequestPayload = {
        include_Assignment_Detail: 0,
        reportID: 0,
        documentID: 0,
        selected_SearchID: selectedSearchIds,
        selected_ActionTypeID: selectedActionTypes,
        saveSearchID: 0,
        reportTypeCode: REPORT_TYPE_CODE,
        showDailyReport: false,
        fromDateTime:
          values.fromDate ??
          new Date(
            new Date().setFullYear(new Date().getFullYear() - 10)
          ).toISOString(),
        toDateTime: values.toDate ?? new Date().toISOString(),
        timeOffset: 0,
        selectedReviewers: selectedUsers.map((u) => u.userId),
        selectedAssignments: [],
        selectedCustodians: [],
        selectedFoldersOrGroups: [],
        selectedAssignmentClassification: [],
        selectedTagClassification: [],
        selectedTags: [],
        searchTermReportType: '',
        pageNumber: -1,
        pageSize: -1,
        isAscending: 'asc',
        sortingColumn: '',
        rowNumber: 0,
        assignmentFlag: false,
        selected_UserID: [],
        selectedSecurityGroups: [],
        selectedDocumentTypes: [],
        selectedRedactionTags: [],
        selectedCommentTypes: [],
        selectedProductions: [],
        isBulkAction: 0,
        selectedSource: '',
        selectedSourceID: [],
        selectedCustomColumns: saveReportPayload.selectedCustomColumns ?? [],
        selectedCustomColumnName: [],
        selectedReviewAssignments: [],
        selectedQCAssignment: [],
        displayBy: 0,
        documentsNotinAnyAssignment: false,
        exportFields: [],
        exportData: [],
        style: '',
        field: '',
        text: '',
        dateStyle: '',
        reportName: '',
        docID: values.docID ? String(values.docID) : '',
        dataAsOf: new Date().toISOString(),
        bulkActionSelectionCriteria: [],
        hugeSearchId: '',
        batchLevel: '',
        filterDocumentGrid: '',
        bulkActionABMAssignment: [],
        bulkActionABMDocument: [],
        navigationSourceContext: '',
        navigationDestinationContext: '',
        isSelectedAll: false,
        subActionType: '',
        showCurrentlyLoggedInUsers:
          selectedOption?.value === 'loginHistory' ? 0 : 1,
        isShared: false,
        isSavedSearchSource: false,
        downloadRequestedOn: new Date().toISOString(),
      };

      const saveResponse = await saveDocumentAuditReportData(savePayload);

      setSaveReportPayload({
        ...savePayload,
        reportID: saveResponse.reportID || 0,
        fromDateTime: savePayload.fromDateTime ?? '',
        toDateTime: savePayload.toDateTime ?? '',
      });

      setReportId(saveResponse.reportID);
      setNotificationId(saveResponse.notificationID);
    } catch (error) {
      setLoading(false);
      console.error('Error initializing report:', error);
    }
  };

  const handleReset = () => {
    setSelectedFilters({});
    setGridData(null);
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);

    const containerActionTypes = Array.isArray(dynamicData?.actionTypes)
      ? dynamicData.actionTypes.map(() => true)
      : [];
    const effectiveActionTypes = Array.isArray(selectedFilters.actionTypes)
      ? selectedFilters.actionTypes
      : containerActionTypes;

    const selectedActionTypes = effectiveActionTypes
      .map((checked: boolean, idx: number) =>
        checked ? dynamicData?.actionTypes?.[idx]?.actionTypeID : null
      )
      .filter((id: string | null) => id !== null);

    // Resolve Search IDs (values.searchList is booleans or IDs)
    const selectedSearchIds = (selectedFilters.searchList || [])
      .map((s: any, idx: number) => {
        if (typeof s === 'object' && s?.id) {
          return Number(s.id);
        }
        if (typeof s === 'boolean' && s) {
          return Number(dynamicData?.searchList[idx]?.id);
        }
        if (typeof s === 'string' || typeof s === 'number') {
          return Number(s);
        }
        return null;
      })
      .filter((id: number | null) => id !== null && !isNaN(id));

    const payload: ExportDocumentAuditReportRequestPayload = {
      progressReportFilteration: {
        docID: `${selectedFilters.docID || 0}`,
        selected_ActionTypeID: selectedActionTypes,
        selected_SearchID: selectedSearchIds,
        reportTypeCode: REPORT_TYPE_CODE,
        timeOffset: 0,
        sortingColumn: 'DocID',
        isAscending: 'asc',
        dataAsOf: new Date().toISOString(),
        selectedCustomColumns: saveReportPayload.selectedCustomColumns ?? [],

        documentID: 0,
        include_Assignment_Detail: 0,
        reportID: saveReportPayload.reportID || 0,
        saveSearchID: 0,
        showDailyReport: false,
        fromDateTime: saveReportPayload.fromDateTime || '',
        toDateTime: saveReportPayload.toDateTime || '',
        selectedReviewers: [],
        selectedAssignments: [],
        selectedCustodians: [],
        selectedFoldersOrGroups: [],
        selectedAssignmentClassification: [],
        selectedTagClassification: [],
        selectedTags: [],
        searchTermReportType: '',
        pageNumber: -1,
        pageSize: -1,
        rowNumber: 0,
        assignmentFlag: false,
        selected_UserID: [],
        selectedSecurityGroups: [],
        selectedDocumentTypes: [],
        selectedRedactionTags: [],
        selectedCommentTypes: [],
        selectedProductions: [],
        isBulkAction: 0,
        selectedSource: '',
        selectedSourceID: [],
        selectedCustomColumnName: [],
        selectedReviewAssignments: [],
        selectedQCAssignment: [],
        displayBy: 0,
        documentsNotinAnyAssignment: false,
        exportFields: [],
        exportData: [],
        style: '',
        field: '',
        text: '',
        dateStyle: '',
        reportName: '',
        bulkActionSelectionCriteria: [],
        hugeSearchId: '',
        batchLevel: '',
        filterDocumentGrid: '',
        bulkActionABMAssignment: [],
        bulkActionABMDocument: [],
        navigationSourceContext: '',
        navigationDestinationContext: '',
        isSelectedAll: false,
        subActionType: '',
        showCurrentlyLoggedInUsers: 0,
        isShared: false,
        isSavedSearchSource: false,
        downloadRequestedOn: new Date().toISOString(),
      },
      actionType: 'EXPORTREPORT',
    };

    try {
      await runDocumentAuditExport(payload);
      showSuccessMessage(ToastMessages.exportHasStarted);
    } catch (err: any) {
      console.error('Export failed:', err);
      showErrorMessage(ToastMessages.exportHasFailed);
    } finally {
      setIsExporting(false);
    }
  };

  const getReportsConfig = () => {
    const config = mergeApiDataIntoConfig(
      reportConfig?.config,
      dynamicData,
      selectedFilters ?? initialValues
    );

    return config;
  };

  const getReportTitle = () => {
    let title = reportConfig!.title;

    if (urlReportName) {
      title = urlReportName;
    }

    return title;
  };

  const loadReportResults = async (
    reportId: number,
    sort?: {columnName?: string; direction?: string}
  ) => {
    setLoading(true);
    try {
      const gridPayload: DocumentAuditReportRequestBody = {
        pageNumber: pageModel.pageNumber,
        reportFilePath: '',
        reportName: '',
        sortingcolumnName: sort?.columnName ?? 'DocID',
        sortingDirection: sort?.direction ?? 'asc',
        reportID: reportId,
      };

      const gridResponse = await getReportGridData(gridPayload);
      const rows = Array.isArray(gridResponse?.data) ? gridResponse.data : [];

      pageModel.totalRecords = gridResponse.totalDisplayRecords;
      setPageModel(pageModel);

      if (rows.length === 0) {
        setGridData({
          totalDisplayRecords: 0,
          data: [],
          noDataMessage: t('backgroundTasks.messageForNoGridData'),
        } as any);
      } else {
        setGridData(gridResponse);
      }
    } catch (error) {
      console.error('Error reloading report:', error);
    } finally {
      setLoading(false);
      setNotificationId(null);
      setReportId(null);
    }
  };

  const resolveSortColumn = (columnName?: string | null): string => {
    const DEFAULT_COLUMN = 'DocID';

    // Validate the input
    if (!columnName) {
      return DEFAULT_COLUMN;
    }

    // Amongst the configured sortable columns, find the column by name
    const sortableColumns = columns.filter((i) => i.sortable === true);
    const sortableColumn = sortableColumns.find((i) => i.field === columnName);
    if (sortableColumn) {
      return sortableColumn.apiName;
    }

    // Could not resolve, return default
    return DEFAULT_COLUMN;
  };

  const resolveSortDirection = (direction?: string | null): string => {
    const DIRECTION_ASCENDING = 'asc';
    const DIRECTION_DESCENDING = 'desc';

    // Validate the input
    if (!direction) {
      return DIRECTION_ASCENDING;
    }

    // Attempt to resolve the direction to a known value
    if (direction === DIRECTION_ASCENDING) {
      return DIRECTION_ASCENDING;
    }
    if (direction === DIRECTION_DESCENDING) {
      return DIRECTION_DESCENDING;
    }

    // Could not resolve, return default
    return DIRECTION_ASCENDING;
  };

  const handlePageChange = (
    pageNumber?: number | null,
    pageSize?: number | null
  ) => {
    setPageModel((prev) => {
      return {
        ...prev,
        pageNumber: pageNumber ?? prev.pageNumber,
        pageSize: pageSize ?? prev.pageSize,
      };
    });
  };

  useEffect(() => {
    // Parse a report ID
    let id = 0;
    if (reportId) {
      id = reportId;
    } else if (urlReportId) {
      id = urlReportId;
    } else if (saveReportPayload.reportID) {
      id = saveReportPayload.reportID;
    }

    // Parse sort
    const column = resolveSortColumn(sortModel[0]?.field);
    const direction = resolveSortDirection(sortModel[0]?.sort);
    const sort = {columnName: column, direction};

    loadReportResults(id, sort);
  }, [sortModel, pageModel]);

  return (
    <>
      {!isInitialized ? (
        <ArrLoadingSpinner show={!isInitialized} overlay={true} isOpen={true} />
      ) : (
        <StandardReports
          className="document-audit"
          title={getReportTitle()}
          showInfoIcon={reportConfig!.showInfoIcon}
          infoContent={reportConfig?.infoContent}
          infoModalTitle={reportConfig?.infoModalTitle ?? ''}
          reportsConfig={getReportsConfig()}
          selectedOption={selectedOption}
          onOptionChange={setSelectedOption}
          onAction={handleAction}
          onColumnToggleAction={handleColumnToggleAction}
          data={gridData}
          gridConfig={{
            primaryKey: 'DocID',
            // Sorting
            sortModel: sortModel,
            pageSizeOptions: [10],
            onSortModelChange: (model) => setSortModel(model),
            // Paging
            totalRecords: pageModel.totalRecords,
            itemsPerPage: pageModel.pageSize,
            currentPage: pageModel.pageNumber,
            showPerPageOptions: true,
            disableColumnResize: false,
            setItemsPerPage: (pageSize) => handlePageChange(null, pageSize),
            onPageChange: (pageNumber, pageSize) =>
              handlePageChange(pageNumber, pageSize),
          }}
          getRowId={(row: any) => row.id}
          isNoData={gridData?.totalDisplayRecords === 0}
          loading={loading}
          columns={columns}
          mapRows={(resp: {
            totalRecords: number;
            totalDisplayRecords: number;
            data: string[][];
          }) =>
            (Array.isArray(resp?.data) ? resp.data : []).map(
              (row: string[], index) => {
                const [docID, action, actionOn, userName, dateTime] = row ?? [];
                return {
                  id: `${docID}-${index}`,
                  docID: docID ?? '',
                  action: action ?? '',
                  actionOn: actionOn ?? '',
                  userName: userName ?? '',
                  dateTime: dateTime ?? '',
                };
              }
            )
          }
          rightPanelHeaderConfig={RIGHT_PANEL_HEADER_CONFIG}
          saveReportConfig={saveReportPayload}
          savedData={leftPanelReport}
        />
      )}

      {notificationId && reportId && (
        <ArrWaitToCompleteModalWrapper
          notificationId={notificationId}
          reportId={reportId}
          onComplete={async (completedReportId) => {
            await loadReportResults(completedReportId);
          }}
          onFlag={() => {
            setLoading(false);
          }}
          loader={true}
          showClose={true}
        />
      )}

      {showToast && (
        <div data-testid="toast-notification">
          <ArrToastNotification
            message={toastMessage}
            status={toastStatus}
            onClose={() => setShowToast(false)}
          />
        </div>
      )}
    </>
  );
};

export default DocumentAuditContainer;
